import { useEffect, useRef, useState } from "react";
import { SOSBeacon } from "../../lib/sosBeacon";

/**
 * Fullscreen red/white alternating flash + Morse SOS audio beacon.
 *
 * Battery notes:
 *  - Flash uses a single `setInterval(500ms)` toggling a CSS class — NOT a
 *    requestAnimationFrame loop. Cheap, deterministic.
 *  - When the beacon is stopped, the interval is cleared and the AudioContext
 *    is fully closed. No idle resources remain.
 */
export function SOSBeaconControl() {
  const beaconRef = useRef(null);
  const flashIntervalRef = useRef(null);
  const [active, setActive] = useState(false);
  const [flashState, setFlashState] = useState("red");

  // Fully clean up on unmount.
  useEffect(() => {
    return () => {
      _stopInternal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function _stopInternal() {
    if (beaconRef.current) {
      beaconRef.current.stop();
      beaconRef.current = null;
    }
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      flashIntervalRef.current = null;
    }
    setFlashState("red");
    setActive(false);
  }

  const start = () => {
    if (active) return;
    const beacon = new SOSBeacon();
    beacon.start();
    beaconRef.current = beacon;
    setActive(true);
    // Deterministic alternating flash. Simple interval = low CPU/battery.
    setFlashState("red");
    flashIntervalRef.current = setInterval(() => {
      setFlashState((prev) => (prev === "red" ? "white" : "red"));
    }, 500);
  };

  const stop = () => {
    _stopInternal();
  };

  return (
    <>
      {/* Fullscreen flash overlay — pointer-events disabled so STOP stays
          clickable. Rendered as a sibling to the main content. */}
      {active && (
        <div
          data-testid="sos-flash-overlay"
          aria-hidden="true"
          className="fixed inset-0 z-[150] pointer-events-none"
          style={{
            backgroundColor: flashState === "red" ? "#FF0000" : "#FFFFFF",
            transition: "background-color 60ms linear",
          }}
        />
      )}

      {!active ? (
        <button
          type="button"
          id="btn-trigger-beacon"
          onClick={start}
          data-testid="sos-trigger-beacon"
          className="sos-trigger-btn w-full inline-flex items-center justify-center gap-2 font-bold tracking-wide uppercase"
        >
          Emitir señal SOS
        </button>
      ) : (
        <button
          type="button"
          id="btn-stop-beacon"
          onClick={stop}
          data-testid="sos-stop-beacon"
          className="sos-stop-btn fixed left-1/2 bottom-10 -translate-x-1/2 z-[200] font-bold tracking-widest uppercase"
        >
          Stop Beacon
        </button>
      )}
    </>
  );
}
